import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Pin, RotateCcw, Send, X } from "lucide-react";
import {
  useCashChat,
  useDashboardCards,
  type CashChatDomain,
  type CashChatMessage,
} from "@/hooks/useCashChat";
import { AssistantChart, type AssistantChartSpec } from "./AssistantChart";
import { MintiMascot } from "./MintiMascot";

const SUGGESTIONS_ALL = [
  "What's the payment outlook for our top debtor?",
  "What's the status of Stark Industrial?",
  "How much was collected last month?",
  "Who owes us the most?",
];

const SUGGESTIONS_RECEIVABLES = [
  "What's the payment outlook for our top debtor?",
  "Will this overdue invoice get paid?",
  "Suggest a status for an overdue invoice with a customer reply",
  "Who owes us the most?",
];

/** Render assistant text, showing ``` fenced blocks as statement cards. */
function AssistantContent({ content }: { content: string }) {
  const segments = content.split("```");
  return (
    <div className="flex flex-col gap-2">
      {segments.map((segment, index) => {
        const key = `${index}-${segment.slice(0, 12)}`;
        if (index % 2 === 1) {
          const lines = segment.replace(/^[a-z]*\n/, "").replace(/\n+$/, "");
          return (
            <pre
              key={key}
              className="overflow-x-auto rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-800"
            >
              {lines}
            </pre>
          );
        }
        const text = segment.trim();
        if (!text) return null;
        return (
          <p key={key} className="whitespace-pre-wrap">
            {text}
          </p>
        );
      })}
    </div>
  );
}

function ChartBlock({
  spec,
  onPin,
}: {
  spec: AssistantChartSpec;
  onPin?: (spec: AssistantChartSpec) => Promise<void>;
}) {
  const [pinState, setPinState] = useState<"idle" | "saving" | "saved" | "error">(
    "idle",
  );
  const [pinError, setPinError] = useState<string | null>(null);

  async function handlePin() {
    if (!onPin) return;
    setPinState("saving");
    setPinError(null);
    try {
      await onPin(spec);
      setPinState("saved");
    } catch (error) {
      setPinState("error");
      setPinError(
        error instanceof Error ? error.message : "Could not pin this chart.",
      );
    }
  }

  return (
    <figure className="mt-2 rounded-xl border border-slate-200 bg-white p-3">
      <figcaption className="mb-2 flex items-start justify-between gap-2">
        <span className="text-xs font-semibold text-slate-800">
          {spec.title}
        </span>
        {onPin ? (
          <button
            type="button"
            onClick={() => void handlePin()}
            disabled={pinState === "saving" || pinState === "saved"}
            className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
              pinState === "saved"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {pinState === "saved" ? (
              <>
                <Check className="h-3 w-3" aria-hidden />
                Pinned
              </>
            ) : (
              <>
                <Pin className="h-3 w-3" aria-hidden />
                {pinState === "saving" ? "Pinning…" : "Pin to dashboard"}
              </>
            )}
          </button>
        ) : null}
      </figcaption>
      <div className="h-52 w-full">
        <AssistantChart spec={spec} />
      </div>
      {pinError ? (
        <p className="mt-1.5 text-[11px] text-red-600">{pinError}</p>
      ) : null}
    </figure>
  );
}

function MessageBubble({
  message,
  onPin,
}: {
  message: CashChatMessage;
  onPin?: (spec: AssistantChartSpec) => Promise<void>;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-emerald-600 px-3 py-2 text-sm text-white">
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="w-full max-w-[95%] rounded-2xl rounded-bl-md bg-slate-100 px-3 py-2 text-sm text-slate-800">
        <AssistantContent content={message.content} />
        {(message.charts ?? []).map((spec, index) => (
          <ChartBlock key={`${spec.title}-${index}`} spec={spec} onPin={onPin} />
        ))}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start" aria-label="Minti is thinking">
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-slate-100 px-3 py-2.5">
        {[0, 1, 2].map((dot) => (
          <span
            key={dot}
            className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
            style={{ animationDelay: `${dot * 150}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

export function CashAssistantPanel({
  domain = "all",
  allowPin,
}: {
  domain?: CashChatDomain;
  /** Defaults to true for domain=all, false for receivables. */
  allowPin?: boolean;
}) {
  const pinEnabled = allowPin ?? domain === "all";
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const { messages, isLoading, error, sendMessage, reset } = useCashChat({
    domain,
  });
  const { pinCard, refresh: refreshCards } = useDashboardCards({
    enabled: pinEnabled,
  });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const suggestions =
    domain === "receivables" ? SUGGESTIONS_RECEIVABLES : SUGGESTIONS_ALL;

  const lastUserPrompt = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === "user") return messages[index].content;
    }
    return null;
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, isLoading, open]);

  function handleSend(text?: string) {
    const value = text ?? draft;
    if (!value.trim()) return;
    setDraft("");
    void sendMessage(value);
  }

  async function handlePin(spec: AssistantChartSpec) {
    await pinCard({
      title: spec.title,
      spec,
      sourcePrompt: lastUserPrompt,
    });
    // Keep a dashboard rendered elsewhere in sync.
    void refreshCards();
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-emerald-600 py-2.5 pl-2.5 pr-5 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-700 hover:shadow-xl"
      >
        <MintiMascot className="h-9 w-9" />
        Ask Minti
      </button>
    );
  }

  const subtitle =
    domain === "receivables"
      ? "Answers come straight from your invoices and collections"
      : "Answers come straight from your invoices and transactions";
  const emptyBlurb =
    domain === "receivables"
      ? "Customer standing, aging, email replies, and payment outlook — ask Minti."
      : "Customer standing, what was collected, cash in and out — or ask for a chart and pin it to your dashboard.";
  const placeholder =
    domain === "receivables"
      ? "Ask about customers, aging, replies, outlook…"
      : "Ask about customers, collections, cash, or charts…";

  return (
    <div
      role="dialog"
      aria-label="Minti"
      className="fixed bottom-5 right-5 z-40 flex h-[40rem] max-h-[calc(100vh-6rem)] w-[30rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
    >
      <header className="flex items-center gap-2.5 border-b border-slate-200 bg-emerald-50/60 px-4 py-3">
        <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-[#E4F5EA]">
          <MintiMascot className="h-9 w-9" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-slate-900">Minti</h2>
          <p className="truncate text-xs text-slate-500">{subtitle}</p>
        </div>
        {messages.length > 0 ? (
          <button
            type="button"
            onClick={reset}
            title="Start a new conversation"
            className="rounded-md p-1.5 text-slate-400 transition hover:bg-white hover:text-slate-600"
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close assistant"
          className="rounded-md p-1.5 text-slate-400 transition hover:bg-white hover:text-slate-600"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </header>

      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
      >
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
            <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-[#E4F5EA]">
              <MintiMascot className="h-14 w-14" />
            </span>
            <div>
              <p className="text-sm font-medium text-slate-800">
                Ask Minti about your data
              </p>
              <p className="mt-1 max-w-[18rem] text-xs text-slate-500">
                {emptyBlurb}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => handleSend(suggestion)}
                  className="rounded-full border border-emerald-200 bg-emerald-50/50 px-3.5 py-1.5 text-xs font-medium text-emerald-700 transition hover:bg-emerald-100"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={`${index}-${message.role}`}
                message={message}
                onPin={pinEnabled ? handlePin : undefined}
              />
            ))}
            {isLoading ? <TypingIndicator /> : null}
            {error ? (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </p>
            ) : null}
          </>
        )}
      </div>

      <form
        className="flex items-end gap-2 border-t border-slate-200 px-3 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          handleSend();
        }}
      >
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleSend();
            }
          }}
          rows={1}
          placeholder={placeholder}
          aria-label="Message Minti"
          className="max-h-24 min-h-[2.5rem] flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none ring-emerald-500 placeholder:text-slate-400 focus:ring-2"
        />
        <button
          type="submit"
          disabled={isLoading || !draft.trim()}
          aria-label="Send"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
        >
          <Send className="h-4 w-4" aria-hidden />
        </button>
      </form>
    </div>
  );
}

